import React, { useState } from "react";
import { ShieldAlert, RotateCw, ExternalLink, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { localizeDict } from "@/lib/localText";
import { YalolaLogo } from "@/components/YalolaLogo";

// Mur plein écran posé par AgeAssuranceGate / Onboarding quand la tranche d'âge Apple bloque l'accès (iOS 26+).
//   • under13  : « Yalola est réservé aux 13 ans et plus » — rien n'est créé ni enregistré côté compte.
//   • required : région où la loi exige la tranche d'âge et partage refusé / indisponible.
// Textes surchargeables Admin › Traductions (préfixe agewall.*).
const L10N = {
  fr: {
    under13_title: "Yalola est réservé aux 13 ans et plus",
    under13_body: "D'après la tranche d'âge partagée par ton appareil (réglages Apple), tu n'as pas encore l'âge requis pour utiliser Yalola. Aucun compte n'a été créé et aucune donnée n'a été enregistrée.",
    under13_hint: "Ce n'est pas ton âge ? La tranche d'âge se gère dans Réglages › ton nom › Famille (ou « Tranche d'âge pour les apps »), puis appuie sur Réessayer.",
    required_title: "Partage de ta tranche d'âge requis",
    required_body: "Dans ta région, la loi demande à Yalola de connaître ta tranche d'âge avant de continuer. Apple ne transmet qu'une tranche (13-15, 16-17, 18+), jamais ta date de naissance.",
    required_hint: "Autorise le partage quand iOS le propose, ou active-le dans Réglages › ton nom › « Tranche d'âge pour les apps », puis appuie sur Réessayer.",
    retry: "Réessayer",
    learn: "Sécurité des enfants",
    signed_out: "Tu as été déconnecté(e) sur cet appareil.",
    detail: "Détail technique",
  },
  en: {
    under13_title: "Yalola is for ages 13 and up",
    under13_body: "According to the age range shared by your device (Apple settings), you are not yet old enough to use Yalola. No account was created and nothing was saved.",
    under13_hint: "Not your age? The age range is managed in Settings › your name › Family (or “Age Range for Apps”), then tap Try again.",
    required_title: "Sharing your age range is required",
    required_body: "In your region, the law requires Yalola to know your age range before continuing. Apple only shares a range (13-15, 16-17, 18+), never your date of birth.",
    required_hint: "Allow sharing when iOS asks, or enable it in Settings › your name › “Age Range for Apps”, then tap Try again.",
    retry: "Try again",
    learn: "Child safety",
    signed_out: "You have been signed out on this device.",
    detail: "Technical detail",
  },
  es: {
    under13_title: "Yalola es para mayores de 13 años",
    under13_body: "Según la franja de edad compartida por tu dispositivo (ajustes de Apple), aún no tienes la edad necesaria para usar Yalola. No se ha creado ninguna cuenta ni guardado ningún dato.",
    under13_hint: "¿No es tu edad? La franja de edad se gestiona en Ajustes › tu nombre › Familia (o «Franja de edad para apps»); luego toca Reintentar.",
    required_title: "Es necesario compartir tu franja de edad",
    required_body: "En tu región, la ley exige que Yalola conozca tu franja de edad antes de continuar. Apple solo comparte una franja (13-15, 16-17, 18+), nunca tu fecha de nacimiento.",
    required_hint: "Permite compartirla cuando iOS lo pida, o actívala en Ajustes › tu nombre › «Franja de edad para apps»; luego toca Reintentar.",
    retry: "Reintentar",
    learn: "Seguridad infantil",
    signed_out: "Se ha cerrado tu sesión en este dispositivo.",
    detail: "Detalle técnico",
  },
  pt: {
    under13_title: "O Yalola é para maiores de 13 anos",
    under13_body: "Segundo a faixa etária partilhada pelo teu dispositivo (definições Apple), ainda não tens a idade necessária para usar o Yalola. Nenhuma conta foi criada e nada foi guardado.",
    under13_hint: "Não é a tua idade? A faixa etária gere-se em Definições › o teu nome › Família (ou «Faixa etária para apps»); depois toca em Tentar novamente.",
    required_title: "É necessário partilhar a tua faixa etária",
    required_body: "Na tua região, a lei exige que o Yalola conheça a tua faixa etária antes de continuar. A Apple só partilha uma faixa (13-15, 16-17, 18+), nunca a tua data de nascimento.",
    required_hint: "Permite a partilha quando o iOS o pedir, ou ativa-a em Definições › o teu nome › «Faixa etária para apps»; depois toca em Tentar novamente.",
    retry: "Tentar novamente",
    learn: "Segurança das crianças",
    signed_out: "A tua sessão foi terminada neste dispositivo.",
    detail: "Detalhe técnico",
  },
};

export const AgeWall = ({ block, onRetry }) => {
  const { lang, config } = useApp();
  const tr = localizeDict(L10N, lang, config?.translations, "agewall");
  const [busy, setBusy] = useState(false);
  const under = block?.verdict === "block_under13";
  const k = under ? "under13" : "required";
  const detail = block?.rec?.error || (block?.rec?.status === "declined" ? "declinedSharing" : "");
  const retry = async () => {
    if (!onRetry || busy) return;
    setBusy(true);
    try { await onRetry(); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[200] flex flex-col overflow-y-auto" style={{ background: "var(--yalola-bg, #F7F3EE)", paddingTop: "var(--sa-top, 0px)", paddingBottom: "var(--sa-bottom, 0px)" }} role="dialog" aria-modal="true" data-testid="age-wall" data-verdict={block?.verdict}>
      <div className="flex items-center gap-2 px-5 pt-5"><YalolaLogo size={30} /><span className="font-bold text-lg" style={{ color: "var(--yalola-text)" }}>Yalola</span></div>
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-md yalola-card p-6 space-y-5" data-testid="age-wall-card">
          <div className="h-14 w-14 rounded-2xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><ShieldAlert size={28} /></div>
          <h1 className="font-bold font-display text-2xl sm:text-3xl" style={{ color: "var(--yalola-text)" }} data-testid="age-wall-title">{tr[`${k}_title`]}</h1>
          <p className="text-base" style={{ color: "var(--yalola-text)" }} data-testid="age-wall-body">{tr[`${k}_body`]}</p>
          {block?.reason === "session" && <p className="text-sm font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="age-wall-signed-out">{tr.signed_out}</p>}
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="age-wall-hint">{tr[`${k}_hint`]}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={retry} disabled={busy} className="pill-primary inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm font-semibold transition-transform active:scale-95 disabled:opacity-60" data-testid="age-wall-retry">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <RotateCw size={16} />} {tr.retry}
            </button>
            <a href="/legal/child-safety" className="inline-flex items-center gap-2 rounded-full px-4 h-11 text-sm font-semibold" style={{ border: "1px solid var(--yalola-border)", color: "var(--yalola-text)" }} data-testid="age-wall-learn">
              <ExternalLink size={16} /> {tr.learn}
            </a>
          </div>
          {detail && <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="age-wall-detail">{tr.detail} : {detail}</p>}
        </div>
      </div>
    </div>
  );
};
